import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const MetricsOverview = ({ players, coaches, goalkeepers, filterRole }) => {
  const stats = useMemo(() => {
    const filteredPlayers = filterRole === 'all' || filterRole === 'player' ? players : [];
    const filteredCoaches = filterRole === 'all' || filterRole === 'coach' ? coaches : [];
    const filteredGoalkeepers = filterRole === 'all' || filterRole === 'goalkeeper' ? goalkeepers : [];

    const totalRecords = filteredPlayers?.length + filteredCoaches?.length + filteredGoalkeepers?.length;
    
    const availableForTransfer = filteredPlayers?.filter(p => p?.availableForTransfer)?.length +
      filteredGoalkeepers?.filter(g => g?.availableForTransfer)?.length;

    const totalGoals = filteredPlayers?.reduce((sum, p) => sum + (p?.goalsScored || 0), 0) +
      filteredGoalkeepers?.reduce((sum, g) => sum + (g?.goalsConceded || 0), 0);

    const totalMatches = filteredPlayers?.reduce((sum, p) => sum + (p?.totalAppearances || 0), 0) +
      filteredCoaches?.reduce((sum, c) => sum + (c?.totalGamesManaged || 0), 0) +
      filteredGoalkeepers?.reduce((sum, g) => sum + (g?.gamesPlayed || 0), 0);

    return [
      {
        id: 1,
        label: 'Total Records',
        value: totalRecords?.toString(),
        change: '+12%',
        trend: 'up',
        icon: 'Database',
        color: 'primary',
        description: 'Active profiles'
      },
      {
        id: 2,
        label: 'Available for Transfer',
        value: availableForTransfer?.toString(),
        change: '+8',
        trend: 'up',
        icon: 'TrendingUp',
        color: 'success',
        description: 'Open to opportunities'
      },
      {
        id: 3,
        label: 'Total Matches',
        value: totalMatches?.toLocaleString(),
        change: 'Combined',
        trend: 'neutral',
        icon: 'Activity',
        color: 'accent',
        description: 'Career appearances'
      },
      {
        id: 4,
        label: 'Performance Metrics',
        value: totalGoals?.toLocaleString(),
        change: 'Goals tracked',
        trend: 'neutral',
        icon: 'Target',
        color: 'warning',
        description: 'Statistical data points'
      }
    ];
  }, [players, coaches, goalkeepers, filterRole]);

  const roleBreakdown = useMemo(() => {
    return [
      {
        role: 'Players',
        count: players?.length,
        icon: 'User',
        color: 'bg-primary/10 text-primary',
        percentage: ((players?.length / (players?.length + coaches?.length + goalkeepers?.length)) * 100)?.toFixed(1)
      },
      {
        role: 'Coaches',
        count: coaches?.length,
        icon: 'Clipboard',
        color: 'bg-secondary/10 text-secondary',
        percentage: ((coaches?.length / (players?.length + coaches?.length + goalkeepers?.length)) * 100)?.toFixed(1)
      },
      {
        role: 'Goalkeepers',
        count: goalkeepers?.length,
        icon: 'Shield',
        color: 'bg-accent/10 text-accent',
        percentage: ((goalkeepers?.length / (players?.length + coaches?.length + goalkeepers?.length)) * 100)?.toFixed(1)
      }
    ];
  }, [players, coaches, goalkeepers]);

  const getColorClasses = (color) => {
    const colors = {
      primary: 'bg-primary/10 text-primary',
      success: 'bg-success/10 text-success',
      accent: 'bg-accent/10 text-accent',
      warning: 'bg-warning/10 text-warning',
      error: 'bg-error/10 text-error'
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-6">Key Performance Indicators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats?.map((stat) => (
            <div
              key={stat?.id}
              className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${getColorClasses(stat?.color)} flex items-center justify-center`}>
                  <Icon name={stat?.icon} size={24} />
                </div>
                {stat?.trend === 'up' && (
                  <div className="flex items-center gap-1 text-success text-sm font-medium">
                    <Icon name="TrendingUp" size={16} />
                    <span>{stat?.change}</span>
                  </div>
                )}
                {stat?.trend === 'neutral' && (
                  <span className="text-muted-foreground text-sm">{stat?.change}</span>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-foreground">{stat?.value}</p>
                <p className="text-sm font-medium text-foreground">{stat?.label}</p>
                <p className="text-xs text-muted-foreground">{stat?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Role Distribution */}
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-6">Talent Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {roleBreakdown?.map((item, index) => (
            <div
              key={index}
              className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${item?.color} flex items-center justify-center`}>
                  <Icon name={item?.icon} size={24} />
                </div>
                <span className="text-2xl font-bold text-foreground">{item?.percentage}%</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1">{item?.role}</h3>
              <p className="text-3xl font-bold text-foreground mb-2">{item?.count}</p>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${item?.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MetricsOverview;